/**
 * LessonMarkdown — rich markdown renderer for curriculum content.
 *
 * Phase 0 (markdown pipeline). Replaces the bespoke SimpleMarkdown /
 * renderProseWithBold approach with a real pipeline:
 *   react-markdown + remark-gfm (tables, task lists, strikethrough)
 *                  + rehype-slug (heading ids for TOC/anchors)
 *                  + rehype-highlight (syntax highlighting)
 *
 * Block elements map to MUI components so they inherit the app theme.
 * Code blocks render as the default <pre><code> (so highlighting is robust)
 * and are styled via the container `& pre` sx — a fixed dark surface with
 * github-dark token colors, consistent in both app modes.
 *
 * NOTE (spike finding): overriding BOTH `pre` and `code` with a custom
 * CodeBlock wrapper truncated react-markdown v10's render at the first code
 * block. Letting the default <pre> render and styling it via container CSS
 * is the robust pattern. A copy-button can be re-added later as a rehype/
 * remark transform or a post-render enhancement rather than a `pre` override.
 */

import * as React from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import rehypeSlug from "rehype-slug";
import { Box, Typography, Link, Divider } from "@mui/material";
import "highlight.js/styles/github-dark.css";
import { rehypeSegmentPractice } from "./LessonMarkdown.rehype";

const MONO = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

// Code blocks use github-dark highlighting (light-colored tokens), so the
// surface MUST stay dark in BOTH app modes. A mode-dependent bg (e.g.
// background.paper, which is #fff in light mode) washes the code out and makes
// the white 10% border invisible — the exact light-mode regression this fixes.
const CODE_SURFACE = "#0d1117";

const components: Components = {
  // `id` is injected onto the heading node by rehype-slug. react-markdown passes
  // it as a prop; we MUST forward it so the TOC anchors + scrollspy have targets.
  // (Forward `id` explicitly rather than spreading props — spreading leaks
  // react-markdown's internal `node` object onto the DOM.)
  h1: ({ children, id }) => (
    <Typography id={id} variant="h4" component="h1" sx={{ fontWeight: 600, letterSpacing: "-0.02em", mt: 4, mb: 2, scrollMarginTop: "96px" }}>
      {children}
    </Typography>
  ),
  h2: ({ children, id }) => (
    <Typography
      id={id}
      variant="h5"
      component="h2"
      sx={{ fontWeight: 600, mt: 4, mb: 1.5, pb: 0.5, borderBottom: "1px solid", borderColor: "divider", scrollMarginTop: "96px" }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children, id }) => (
    <Typography id={id} variant="h6" component="h3" sx={{ fontWeight: 600, mt: 3, mb: 1, scrollMarginTop: "96px" }}>
      {children}
    </Typography>
  ),
  h4: ({ children, id }) => (
    <Typography id={id} variant="subtitle1" component="h4" sx={{ fontWeight: 600, mt: 2.5, mb: 1, scrollMarginTop: "96px" }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
      {children}
    </Typography>
  ),
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 500 }}>
      {children}
    </Link>
  ),
  ul: ({ children }) => <Box component="ul" sx={{ pl: 3, mb: 2, "& li": { mb: 0.75 } }}>{children}</Box>,
  ol: ({ children }) => <Box component="ol" sx={{ pl: 3, mb: 2, "& li": { mb: 0.75 } }}>{children}</Box>,
  li: ({ children }) => <Box component="li" sx={{ lineHeight: 1.75 }}>{children}</Box>,
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{
        m: 0,
        my: 2,
        pl: 2,
        py: 0.5,
        borderLeft: "3px solid",
        borderColor: "primary.main",
        bgcolor: "action.hover",
        borderRadius: "0 8px 8px 0",
        "& p": { mb: 0 },
      }}
    >
      {children}
    </Box>
  ),
  hr: () => <Divider sx={{ my: 3 }} />,
  table: ({ children }) => (
    <Box sx={{ overflowX: "auto", my: 2 }}>
      <Box
        component="table"
        sx={{
          borderCollapse: "collapse",
          width: "100%",
          fontSize: "0.9rem",
          "& th, & td": { border: "1px solid", borderColor: "divider", px: 1.5, py: 1, textAlign: "left" },
          "& th": { bgcolor: "action.hover", fontWeight: 500 },
        }}
      >
        {children}
      </Box>
    </Box>
  ),
  // Only override INLINE code → theme-aware chip. Block code (language-* class)
  // passes through to the default <pre><code>, styled by the container below.
  code: ({ className, children }) => {
    if (className && className.includes("language-")) {
      return <code className={className}>{children}</code>;
    }
    return (
      <Box
        component="code"
        sx={{
          fontFamily: MONO,
          fontSize: "0.85em",
          px: 0.6,
          py: 0.15,
          borderRadius: 0.75,
          bgcolor: "action.selected",
          color: "text.primary",
        }}
      >
        {children}
      </Box>
    );
  },
};

export interface LessonMarkdownProps {
  children: string;
  /** v2: segment anchors after which to inject an inline practice card. */
  practiceAnchors?: string[];
  /** v2: render the practice card for a given segment anchor. */
  renderSegmentPractice?: (anchor: string) => React.ReactNode;
  /** v2: render the slide strip for a given segment anchor (top-of-segment). */
  renderSegmentSlides?: (anchor: string) => React.ReactNode;
}

const LessonMarkdown: React.FC<LessonMarkdownProps> = ({
  children,
  practiceAnchors,
  renderSegmentPractice,
  renderSegmentSlides,
}) => {
  // Scope the dark code-block treatment to MARKDOWN code blocks only — those
  // render as <pre><code>. Injected practice widgets (e.g. DebugFeedbackBlock)
  // use a bare <pre> with no <code> child; without this guard the rule bled
  // onto them, turning the debug snippet into a black box with invisible text.
  const containerSx = {
    "& > :first-of-type": { mt: 0 },
    "& pre:has(code)": {
      m: 0,
      my: 2.5,
      p: 2,
      borderRadius: 2,
      bgcolor: CODE_SURFACE,
      border: "1px solid rgba(255,255,255,0.10)",
      overflowX: "auto",
      fontSize: "0.85rem",
      lineHeight: 1.6,
    },
    "& pre code": { fontFamily: MONO, background: "transparent", p: 0, fontSize: "inherit" },
  } as const;

  const injecting = (!!renderSegmentPractice || !!renderSegmentSlides) && !!practiceAnchors?.length;

  const rehypePlugins: any[] = injecting
    ? [rehypeSlug, [rehypeSegmentPractice, { anchors: practiceAnchors }], rehypeHighlight]
    : [rehypeSlug, rehypeHighlight];

  const mdComponents = injecting
    ? ({
        ...components,
        "segment-practice": ({ node }: any) => {
          const anchor = node?.properties?.anchor;
          return anchor && renderSegmentPractice ? <>{renderSegmentPractice(anchor)}</> : null;
        },
        "segment-slides": ({ node }: any) => {
          const anchor = node?.properties?.anchor;
          return anchor && renderSegmentSlides ? <>{renderSegmentSlides(anchor)}</> : null;
        },
      } as Components)
    : components;

  return (
    <Box sx={containerSx}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={rehypePlugins} components={mdComponents}>
        {children}
      </ReactMarkdown>
    </Box>
  );
};

export default LessonMarkdown;
